import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Lightbulb } from "@phosphor-icons/react";
import { Section, Pill, EmptyState } from "./_shared";
import { AIAssistant } from "./_ai";

export default function SolutionLens({ projectId }) {
  const [risks, setRisks] = useState([]);
  const [issues, setIssues] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genTarget, setGenTarget] = useState(null); // {type, id}

  const load = async () => {
    setLoading(true);
    try {
      const [rRes, iRes, sRes] = await Promise.all([
        api.get(`/projects/${projectId}/risks`),
        api.get(`/projects/${projectId}/issues`),
        api.get(`/projects/${projectId}/solutions`),
      ]);
      setRisks(rRes.data); setIssues(iRes.data); setSolutions(sRes.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [projectId]);

  const generate = async (target_type, target_id) => {
    setGenTarget({ type: target_type, id: target_id });
    try {
      const { data } = await api.post(`/projects/${projectId}/solutions/generate`, { target_type, target_id });
      setSolutions((prev) => [
        ...prev.filter(s => !(s.target_type === target_type && s.target_id === target_id)),
        ...data,
      ]);
      toast.success("3 solutions generated");
    } catch { toast.error("Generation failed"); }
    finally { setGenTarget(null); }
  };

  const byTarget = useMemo(() => {
    const m = new Map();
    solutions.forEach(s => {
      const k = `${s.target_type}:${s.target_id}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(s);
    });
    return m;
  }, [solutions]);

  const TargetBlock = ({ type, item }) => {
    const k = `${type}:${item.id}`;
    const sols = byTarget.get(k) || [];
    const busy = genTarget && genTarget.type === type && genTarget.id === item.id;
    return (
      <div className="p-5 border border-zinc-200 bg-white" data-testid={`solution-target-${item.id}`}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Pill tone={type === "risk" ? "red" : "orange"}>{type}</Pill>
              <h3 className="font-display font-bold text-lg">{item.title}</h3>
            </div>
            {item.description && <p className="text-sm text-zinc-600 mt-1">{item.description}</p>}
          </div>
          <button onClick={() => generate(type, item.id)} disabled={busy}
            className="shrink-0 inline-flex items-center gap-2 h-10 px-4 bg-[#0020F5] text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-50"
            data-testid={`generate-solutions-${item.id}`}>
            <Sparkle size={14} weight="fill" />
            {busy ? "Generating…" : (sols.length ? "Regenerate 3 solutions" : "Generate 3 solutions")}
          </button>
        </div>

        {sols.length > 0 && (
          <div className="mt-5 grid lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            {sols.map((s, idx) => (
              <div key={s.id} className="bg-white p-5" data-testid={`solution-card-${s.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-400">Option {String(idx + 1).padStart(2, "0")}</div>
                  <Lightbulb size={16} weight="duotone" className="text-[#0020F5]" />
                </div>
                <h4 className="font-display font-bold text-base mt-2">{s.title}</h4>
                <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{s.description}</p>
                <div className="mt-3 flex items-center gap-2">
                  <Pill tone={s.effort === "high" ? "red" : s.effort === "medium" ? "orange" : "teal"}>Effort: {s.effort}</Pill>
                  <Pill tone={s.impact === "high" ? "blue" : s.impact === "medium" ? "orange" : "default"}>Impact: {s.impact}</Pill>
                </div>
                {s.steps?.length > 0 && (
                  <div className="mt-4">
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">Steps</div>
                    <ol className="mt-2 text-sm text-zinc-700 space-y-1 list-decimal list-inside">
                      {s.steps.map((st, k) => <li key={`step-${k}-${(st || "").slice(0, 30)}`}>{st}</li>)}
                    </ol>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="solution-lens">
      <Section eyebrow="Mitigate" title="Solutions for risks">
        {loading ? <div className="text-zinc-500 text-sm">Loading…</div> :
          risks.length === 0 ? <EmptyState title="No risks to mitigate" hint="Add risks in the Risk Lens to generate solutions." testid="empty-solution-risks" /> :
          <div className="space-y-4">
            {risks.map(r => <TargetBlock key={r.id} type="risk" item={r} />)}
          </div>
        }
      </Section>

      <Section eyebrow="Resolve" title="Solutions for issues">
        {loading ? <div className="text-zinc-500 text-sm">Loading…</div> :
          issues.length === 0 ? <EmptyState title="No issues to resolve" hint="Add issues in the Issue Lens to generate solutions." testid="empty-solution-issues" /> :
          <div className="space-y-4">
            {issues.map(i => <TargetBlock key={i.id} type="issue" item={i} />)}
          </div>
        }
      </Section>
      <AIAssistant projectId={projectId} lens="solution" label="Solution" />
    </div>
  );
}
